import React, { useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Space, message, Divider, Tooltip } from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import { PlusOutlined, FileTextOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { featureConfigsApi } from '../services/api-services';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import 'react-markdown-editor-lite/lib/index.css';

const mdParser = new MarkdownIt();

// ---- INI 解析 / 序列化 ----
interface IniEntry { key: string; label: string; value: string; }
interface IniSection { name: string; entries: IniEntry[]; }

function parseIniToSections(ini: string): IniSection[] {
  const sections: IniSection[] = [];
  let current: IniSection | null = null;
  let labelMap: Record<string, string> = {};

  for (const line of ini.split('\n')) {
    const t = line.trim();
    const secMatch = t.match(/^\[(.+)\]$/);
    if (secMatch) {
      current = { name: secMatch[1], entries: [] };
      labelMap = {};
      sections.push(current);
      continue;
    }
    // 注释行 # key=label;key2=label2
    if ((t.startsWith('#') || t.startsWith(';')) && current) {
      const commentText = t.replace(/^[#;]\s?/, '');
      for (const part of commentText.split(';')) {
        const m = part.trim().match(/^([^=]+?)=(.+)$/);
        if (m) labelMap[m[1].trim()] = m[2].trim();
      }
      continue;
    }
    const kvMatch = t.match(/^([^=]+?)=(.*)$/);
    if (kvMatch && current) {
      current.entries.push({ key: kvMatch[1].trim(), label: labelMap[kvMatch[1].trim()] || '', value: kvMatch[2].trim() });
    }
  }
  return sections;
}

function serializeSectionsToIni(sections: IniSection[]): string {
  return sections.map((s) => {
    const hasLabels = s.entries.some((e) => e.label);
    let block = `[${s.name}]\n`;
    if (hasLabels) {
      const labelLine = s.entries.filter((e) => e.label).map((e) => `${e.key}=${e.label}`).join(';');
      block += `# ${labelLine}\n`;
    }
    block += s.entries.map((e) => `${e.key}=${e.value}`).join('\n');
    return block;
  }).join('\n\n') + '\n';
}

// ---- 可视化 Section 编辑器 ----
// 单 Section 编辑器
const SectionEditor: React.FC<{ section: IniSection; onChange: (s: IniSection) => void }> = ({ section, onChange }) => {
  const updateEntry = (ei: number, patch: Partial<IniEntry>) =>
    onChange({ ...section, entries: section.entries.map((e, i) => i === ei ? { ...e, ...patch } : e) });
  const addEntry = () => onChange({ ...section, entries: [...section.entries, { key: '', label: '', value: '' }] });
  const removeEntry = (ei: number) => onChange({ ...section, entries: section.entries.filter((_, i) => i !== ei) });

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 28px', gap: 8, marginBottom: 4 }}>
        {['Key（变量名）', '标签名称', '默认值'].map((h) => (
          <span key={h} style={{ fontSize: 11, color: 'var(--muted-text)' }}>{h}</span>
        ))}
        <span />
      </div>
      {section.entries.map((entry, ei) => (
        <div key={ei} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 28px', gap: 8, marginBottom: 6 }}>
          <Input size="small" value={entry.key} placeholder="配置项的KEY" onChange={(e) => updateEntry(ei, { key: e.target.value })} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          <Input size="small" value={entry.label} placeholder="标签名称" onChange={(e) => updateEntry(ei, { label: e.target.value })} />
          <Input size="small" value={entry.value} placeholder="（留空）" onChange={(e) => updateEntry(ei, { value: e.target.value })} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => removeEntry(ei)} />
        </div>
      ))}
      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addEntry} style={{ marginTop: 4, width: '100%' }}>添加字段</Button>
    </div>
  );
};

// ---- 主页面 ----
const FeatureConfigManagement: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form] = Form.useForm();
  const [section, setSection] = useState<IniSection>({ name: '', entries: [] });
  const [docModalVisible, setDocModalVisible] = useState(false);
  const [docEditing, setDocEditing] = useState<any>(null);
  const [docContent, setDocContent] = useState('');

  const load = async () => { try { const res: any = await featureConfigsApi.list(); setList(res); } catch {} };
  useEffect(() => { load(); }, []);

  const handleSave = async (values: any) => {
    const config_ini = section.entries.length > 0 ? serializeSectionsToIni([{ ...section, name: values.name }]) : '';
    try {
      if (editing) { await featureConfigsApi.update(editing.id, { ...values, config_ini }); message.success('更新成功'); }
      else { await featureConfigsApi.create({ ...values, config_ini }); message.success('创建成功'); }
      setModalVisible(false); form.resetFields(); setEditing(null); setSection({ name: '', entries: [] }); load();
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const handleDelete = async (id: number) => {
    try { await featureConfigsApi.delete(id); message.success('删除成功'); load(); } catch { message.error('删除失败'); }
  };

  const openEdit = (r: any) => {
    setEditing(r);
    form.setFieldsValue({ name: r.name, version: r.version });
    const parsed = r.config_ini ? parseIniToSections(r.config_ini) : [];
    setSection(parsed[0] || { name: r.name || '', entries: [] });
    setModalVisible(true);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setSection({ name: '', entries: [] });
    setModalVisible(true);
  };

  const openDocEditor = (r: any) => {
    setDocEditing(r);
    setDocContent(r.config_doc || '');
    setDocModalVisible(true);
  };

  const handleSaveDoc = async () => {
    try {
      await featureConfigsApi.update(docEditing.id, { config_doc: docContent });
      message.success('配置说明保存成功');
      setDocModalVisible(false);
      load();
    } catch { message.error('保存失败'); }
  };

  const columns = [
    { title: '功能名称', dataIndex: 'name', key: 'name' },
    { title: '版本', dataIndex: 'version', key: 'version', width: 80 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 180, render: (v: string) => v ? new Date(v).toLocaleString() : '-' },
    { title: '操作', key: 'action', width: 200, render: (_: any, r: any) => (
      <Space>
        <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
        <Button type="link" size="small" icon={<FileTextOutlined />} onClick={() => openDocEditor(r)}>配置说明</Button>
        <Popconfirm title="确定删除？" onConfirm={() => handleDelete(r.id)} okText="删除" okButtonProps={{ danger: true }} cancelText="取消">
          <Button type="link" danger size="small">删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="apple-page-title" style={{ marginBottom: 0 }}>功能配置</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增</Button>
      </div>
      <ResponsiveTable dataSource={list} columns={columns} rowKey="id" pagination={false} />

      {/* 编辑弹窗 */}
      <Modal
        title={editing ? '编辑功能配置' : '新增功能配置'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); form.resetFields(); setSection({ name: '', entries: [] }); }}
        onOk={() => form.submit()}
        width={780}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="name" label="功能名称" rules={[{ required: true, message: '请输入名称' }]} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="version" label="版本" initialValue={1} style={{ width: 100 }}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Form>


        <SectionEditor section={section} onChange={setSection} />


      </Modal>

      {/* 配置说明弹窗 */}
      <Modal
        title={`配置说明 - ${docEditing?.name || ''}`}
        open={docModalVisible}
        onCancel={() => setDocModalVisible(false)}
        onOk={handleSaveDoc}
        okText="保存"
        width="80vw"
        styles={{ body: { padding: 0 } }}
        destroyOnHidden
      >
        <MdEditor
          value={docContent}
          style={{ height: '60vh' }}
          renderHTML={(text) => mdParser.render(text)}
          onChange={({ text }) => setDocContent(text)}
        />
      </Modal>
    </div>
  );
};

export default FeatureConfigManagement;
